import {Coordinates} from "sigma/types";
import {Dataset, Region} from "../types/Types";
import {OverlayResults} from "../types/overlay";
import {pvalueToAlphaLog} from "../utils/colour";
import {getDatasetBounds} from "../utils/dataset";
import {makeCoordinateMapper, scaleCoordinate, scaleNumber} from "../utils/drawOverlay";
import {drawSvgLegend} from "./svgLegend";
import {createSvg, serializeSvg} from "./svgUtils";

function buildSvgPath(vertices: Coordinates[]): string {
  if (vertices.length < 3) return "";
  const last = vertices[vertices.length - 1];
  const first = vertices[0];
  let d = `M ${(last.x + first.x) / 2} ${(last.y + first.y) / 2}`;
  for (let i = 0; i < vertices.length - 1; i++) {
    const cur = vertices[i];
    const next = vertices[i + 1];
    d += ` Q ${cur.x} ${cur.y} ${(cur.x + next.x) / 2} ${(cur.y + next.y) / 2}`;
  }
  d += ` Q ${last.x} ${last.y} ${(first.x + last.x) / 2} ${(first.y + last.y) / 2} Z`;
  return d;
}

const useOverlaySvg = (
  overlayResults: OverlayResults | undefined,
  dataset: Dataset | undefined,
  regions: Region[] | undefined | null,
) => {
  return (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const bounds = getDatasetBounds(dataset);
      if (!bounds) {
        reject(new Error("Dataset bounds not available"));
        return;
      }

      const container = document.getElementById("page-content");
      if (!container) {
        reject(new Error("#page-content not found"));
        return;
      }

      const width = container.offsetWidth;
      const height = container.offsetHeight;
      const dpr = window.devicePixelRatio || 1;

      // Virtual canvas: buffer sized like the real canvas so makeCoordinateMapper
      // produces CSS-pixel coordinates after its internal /dpr division.
      const virtualCanvas = {width: width * dpr, height: height * dpr} as HTMLCanvasElement;
      const mapToCanvas = makeCoordinateMapper(bounds, virtualCanvas);

      const svg = createSvg(width, height);

      // Network outline
      const centre = mapToCanvas({
        x: bounds.minX + (bounds.maxX - bounds.minX) / 2,
        y: bounds.minY + (bounds.maxY - bounds.minY) / 2,
      });
      const viewportRadius = scaleCoordinate(mapToCanvas, {
        x: (bounds.maxX - bounds.minX) / 2,
        y: (bounds.maxY - bounds.minY) / 2,
      }) * 1.1;

      const outlineEl = document.createElementNS(svg.namespaceURI, "circle");
      outlineEl.setAttribute("cx", String(centre.x));
      outlineEl.setAttribute("cy", String(centre.y));
      outlineEl.setAttribute("r", String(viewportRadius));
      outlineEl.setAttribute("fill", "none");
      outlineEl.setAttribute("stroke", "gray");
      outlineEl.setAttribute("stroke-width", "1");
      outlineEl.setAttribute("stroke-dasharray", "5 5");
      svg.appendChild(outlineEl);

      if (regions) {
        const regionLookup = Object.fromEntries(regions.map(r => [r.name, r]));
        const enrichedNames = new Set(
          Object.keys(overlayResults?.enrichment?.enriched_domains ?? {})
        );

        // Non-enriched regions — black fill, dashed gray stroke
        regions
          .filter(r => !enrichedNames.has(r.name.replaceAll("\n", " ")))
          .forEach(region => {
            const vertices = region.outline.map(v => mapToCanvas(v as Coordinates));
            const d = buildSvgPath(vertices);
            if (!d) return;
            const path = document.createElementNS(svg.namespaceURI, "path");
            path.setAttribute("d", d);
            path.setAttribute("fill", "#000");
            path.setAttribute("stroke", "gray");
            path.setAttribute("stroke-width", "1");
            path.setAttribute("stroke-dasharray", "5 5");
            svg.appendChild(path);
          });

        if (overlayResults) {
          // Enriched regions — colored stroke, black fill
          Object.keys(overlayResults.enrichment?.enriched_domains ?? {}).forEach((domain: string) => {
            const region = regionLookup[domain];
            if (!region) return;
            const vertices = region.outline.map(v => mapToCanvas(v as Coordinates));
            const d = buildSvgPath(vertices);
            if (!d) return;
            const path = document.createElementNS(svg.namespaceURI, "path");
            path.setAttribute("d", d);
            path.setAttribute("fill", "#000");
            path.setAttribute("stroke", region.colour);
            path.setAttribute("stroke-width", "3");
            svg.appendChild(path);
          });

          // Overlay dots
          const geneToNode = Object.fromEntries(
            dataset?.nodes.map(node => [node.n, node]) ?? []
          );
          const dotRadius = scaleNumber(mapToCanvas, 50);

          overlayResults.overlay.forEach(overlayResult => {
            overlayResult.genes.forEach(gene => {
              const node = geneToNode[gene.name];
              if (!node) return;
              const {x, y} = mapToCanvas(node as Coordinates);
              const circle = document.createElementNS(svg.namespaceURI, "circle");
              circle.setAttribute("cx", String(x));
              circle.setAttribute("cy", String(y));
              circle.setAttribute("r", String(dotRadius));
              circle.setAttribute("fill", overlayResult.colour);
              circle.setAttribute("fill-opacity", String(pvalueToAlphaLog(gene.pvalue, 0.1, 1)));
              svg.appendChild(circle);
            });
          });
        }
      }

      // Legend (bottom-left) — only when overlay results are available
      if (overlayResults && regions) {
        const regionLookup = Object.fromEntries(regions.map(r => [r.name, r]));
        const overlayItems = overlayResults.overlay.map(r => ({
          type: "circle" as const, colour: r.colour, label: r.verbose_name,
        }));
        const domainItems = Object.keys(overlayResults.enrichment?.enriched_domains ?? {})
          .filter(d => regionLookup[d])
          .map(d => ({type: "line" as const, colour: regionLookup[d].colour, label: d}));

        drawSvgLegend(svg as SVGSVGElement, height, "Bioprocess-Enriched Network Regions", [overlayItems, domainItems]);
      }

      resolve(serializeSvg(svg));
    });
  };
};

export default useOverlaySvg;
